class OrlariV2Product {
  constructor(root) {
    this.root = root;
    this.maxFileSize = 12 * 1024 * 1024;
    this.maxImageEdge = 1600;
    this.imageCompressionQuality = .84;
    this.previewUrl = null;
    this.preparedFile = null;
    this.uploadEndpoint = this.root.dataset.photoUploadEndpoint || '';
    this.uploadPromise = null;
    this.uploadedPhotoUrl = '';
    this.uploadState = 'idle';
    this.uploadAbortController = null;
    this.uploadTimeoutId = null;
    this.uploadSlowNoticeId = null;
    this.uploadErrorMessage = '';
    this.uploadRequestId = 0;
    this.selectionRequestId = 0;
    this.submitting = false;
    this.handleFileChange = this.handleFileChange.bind(this);
    this.retryPhotoUpload = this.retryPhotoUpload.bind(this);
    this.guardPersonalization = this.guardPersonalization.bind(this);
    this.handleStickyClick = this.handleStickyClick.bind(this);
    this.updateStickyVisibility = this.updateStickyVisibility.bind(this);
    this.connectedCallback();
  }

  querySelector(selector) {
    return this.root.querySelector(selector);
  }

  connectedCallback() {
    this.fileInput = this.querySelector('[data-orlari-file-input]');
    this.dropzone = this.querySelector('[data-orlari-dropzone]');
    this.emptyState = this.querySelector('[data-orlari-empty]');
    this.preview = this.querySelector('[data-orlari-preview]');
    this.previewImage = this.querySelector('[data-orlari-preview-image]');
    this.fileName = this.querySelector('[data-orlari-file-name]');
    this.status = this.querySelector('[data-orlari-status]');
    this.uploadRetry = this.querySelector('[data-orlari-upload-retry]');
    this.submitButton = this.querySelector('[id^="ProductSubmitButton-"]');
    this.paymentButton = this.querySelector('.shopify-payment-button');
    this.sticky = this.querySelector('[data-orlari-sticky]');
    this.stickyButton = this.querySelector('[data-orlari-sticky-button]');
    this.stickyPrice = this.querySelector('[data-orlari-sticky-price]');
    this.deliveryEstimate = this.querySelector('[data-orlari-delivery-range]');
    this.defaultGuidance = this.status?.textContent.trim() || '';

    this.updateDeliveryEstimate();

    if (!this.fileInput || !this.submitButton) return;

    this.prepareUploadConnection();
    this.form = document.getElementById(this.fileInput.dataset.productFormId);
    this.defaultSubmitText = window.variantStrings?.addToCart || 'Add to cart';

    this.fileInput.addEventListener('change', this.handleFileChange);
    this.uploadRetry?.addEventListener('click', this.retryPhotoUpload);
    this.form?.addEventListener('submit', this.guardPersonalization, true);
    this.stickyButton?.addEventListener('click', this.handleStickyClick);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== this.root.dataset.section) return;
        window.setTimeout(() => this.syncPurchaseState(), 0);
      });
    }

    window.addEventListener('scroll', this.updateStickyVisibility, { passive: true });
    window.addEventListener('resize', this.updateStickyVisibility, { passive: true });

    this.syncPurchaseState();
    this.updateStickyVisibility();
  }

  prepareUploadConnection() {
    if (!this.uploadEndpoint) return;
    try {
      const endpoint = new URL(this.uploadEndpoint, window.location.href);
      if (endpoint.protocol !== 'https:') return;
      const existing = Array.from(document.querySelectorAll('link[rel="preconnect"]'))
        .some((link) => link.href === `${endpoint.origin}/`);
      if (existing) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = endpoint.origin;
      link.crossOrigin = 'anonymous';
      document.head.append(link);
    } catch (error) {
      return;
    }
  }

  updateDeliveryEstimate() {
    if (!this.deliveryEstimate) return;

    const deliveryItem = this.deliveryEstimate.closest('[data-orlari-delivery]');
    const minimumDays = Number.parseInt(deliveryItem?.dataset.minBusinessDays || '8', 10);
    const windowDays = Number.parseInt(deliveryItem?.dataset.deliveryWindowDays || '2', 10);

    if (!Number.isFinite(minimumDays) || !Number.isFinite(windowDays) || minimumDays < 0 || windowDays < 0) return;

    const earliest = this.addBusinessDays(new Date(), minimumDays);
    const latest = new Date(earliest);
    latest.setDate(latest.getDate() + windowDays);
    const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const dayOnly = new Intl.DateTimeFormat('en-US', { day: 'numeric' });
    const monthDayYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    if (earliest.getFullYear() !== latest.getFullYear()) {
      this.deliveryEstimate.textContent = `${monthDayYear.format(earliest)}–${monthDayYear.format(latest)}`;
    } else if (earliest.getMonth() === latest.getMonth()) {
      this.deliveryEstimate.textContent = `${monthDay.format(earliest)}–${dayOnly.format(latest)}`;
    } else {
      this.deliveryEstimate.textContent = `${monthDay.format(earliest)}–${monthDay.format(latest)}`;
    }
  }

  addBusinessDays(date, days) {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }

    return result;
  }

  destroy() {
    this.destroyed = true;
    this.selectionRequestId += 1;
    this.cancelPhotoUpload();
    this.preparedFile = null;
    this.cartAbortController?.abort();
    if (this.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
    this.variantChangeUnsubscriber?.();
    this.fileInput?.removeEventListener('change', this.handleFileChange);
    this.uploadRetry?.removeEventListener('click', this.retryPhotoUpload);
    this.form?.removeEventListener('submit', this.guardPersonalization, true);
    this.stickyButton?.removeEventListener('click', this.handleStickyClick);
    window.removeEventListener('scroll', this.updateStickyVisibility);
    window.removeEventListener('resize', this.updateStickyVisibility);
  }

  async handleFileChange() {
    const file = this.fileInput.files?.[0];
    this.clearStatusClasses();

    if (!file) {
      this.resetPhoto();
      return;
    }

    if (!file.type.startsWith('image/') && !file.name.toLowerCase().endsWith('.heic')) {
      this.showError('Please choose a JPG, PNG, WEBP, or HEIC image.');
      this.fileInput.value = '';
      this.resetPhoto(false);
      return;
    }

    if (file.size > this.maxFileSize) {
      this.showError('This photo is larger than 12 MB. Please choose a smaller image.');
      this.fileInput.value = '';
      this.resetPhoto(false);
      return;
    }

    const selectionRequestId = ++this.selectionRequestId;
    this.cancelPhotoUpload();
    this.preparedFile = null;
    this.syncPurchaseState();
    this.publishEvent('photo_selected', {
      file_type: file.type || 'image/heic',
      size_bucket: this.getFileSizeBucket(file.size)
    });
    this.setBusyStatus(this.root.dataset.photoPreparing || 'Preparing your photo…');

    let preparedFile = file;
    try {
      preparedFile = await this.prepareImage(file);
    } catch (error) {
      preparedFile = file;
    }

    if (selectionRequestId !== this.selectionRequestId) return;
    this.finalizePhoto(preparedFile);
  }

  finalizePhoto(file) {
    this.preparedFile = file;
    this.showPreview(file);
    this.dropzone.classList.add('is-ready');
    this.dropzone.classList.remove('is-error');
    this.syncPurchaseState();
    this.startPhotoUpload(file);
  }

  cancelPhotoUpload() {
    this.uploadRequestId += 1;
    this.clearUploadTimers();
    this.uploadAbortController?.abort();
    this.uploadAbortController = null;
    this.uploadPromise = null;
    this.uploadedPhotoUrl = '';
    this.uploadState = 'idle';
    this.uploadErrorMessage = '';
    if (this.uploadRetry) this.uploadRetry.hidden = true;
  }

  clearUploadTimers() {
    window.clearTimeout(this.uploadTimeoutId);
    window.clearTimeout(this.uploadSlowNoticeId);
    this.uploadTimeoutId = null;
    this.uploadSlowNoticeId = null;
  }

  retryPhotoUpload() {
    if (this.submitting || this.uploadState !== 'error' || !this.preparedFile) return;
    this.startPhotoUpload(this.preparedFile);
  }

  setBusyStatus(message) {
    this.clearStatusClasses();
    this.status.textContent = message;
    this.status.classList.add('is-busy');
  }

  updateUploadRetry() {
    if (this.uploadRetry) {
      this.uploadRetry.hidden = this.uploadState !== 'error' || !this.preparedFile || !this.uploadEndpoint || this.submitting;
    }
  }

  startPhotoUpload(file) {
    if (!file || !this.uploadEndpoint) {
      this.uploadState = 'error';
      this.showError('Photo upload is unavailable. Please refresh the page and try again.');
      return Promise.resolve(null);
    }

    this.clearUploadTimers();
    this.uploadAbortController?.abort();
    const controller = new AbortController();
    const requestId = ++this.uploadRequestId;
    this.uploadAbortController = controller;
    this.uploadedPhotoUrl = '';
    this.uploadState = 'uploading';
    this.uploadErrorMessage = '';
    this.setBusyStatus(this.root.dataset.photoUploading || 'Uploading your photo…');
    let timedOut = false;
    this.uploadSlowNoticeId = window.setTimeout(() => {
      if (requestId !== this.uploadRequestId) return;
      this.setBusyStatus(this.root.dataset.photoUploadSlow || 'Still uploading. Please keep this page open.');
    }, 10000);
    this.uploadTimeoutId = window.setTimeout(() => {
      if (requestId !== this.uploadRequestId) return;
      timedOut = true;
      controller.abort();
    }, 30000);

    const uploadContentType = file.type || (file.name.toLowerCase().endsWith('.heic') ? 'image/heic' : 'image/jpeg');

    this.uploadPromise = fetch(this.uploadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': uploadContentType
      },
      body: file,
      signal: controller.signal
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.url) {
          throw new Error(payload.error || 'We could not upload your photo.');
        }
        return payload.url;
      })
      .then((photoUrl) => {
        if (requestId !== this.uploadRequestId) return null;
        this.uploadedPhotoUrl = photoUrl;
        this.uploadState = 'ready';
        this.uploadAbortController = null;
        this.clearStatusClasses();
        this.status.textContent = 'Photo ready';
        this.status.classList.add('is-success');
        this.dropzone.classList.remove('is-error');
        this.publishEvent('photo_ready');
        return photoUrl;
      })
      .catch((error) => {
        if (requestId !== this.uploadRequestId || (error?.name === 'AbortError' && !timedOut)) return null;
        this.uploadState = 'error';
        this.uploadAbortController = null;
        this.uploadErrorMessage = timedOut
          ? this.root.dataset.photoUploadTimeout || 'Upload timed out. Your photo is still selected. Please try again.'
          : this.root.dataset.photoUploadFailed || 'Your photo could not be uploaded. Check your connection and try again.';
        this.showError(this.uploadErrorMessage);
        this.publishEvent('photo_upload_failed');
        return null;
      })
      .finally(() => {
        if (requestId === this.uploadRequestId) this.clearUploadTimers();
      });

    return this.uploadPromise;
  }

  getFileSizeBucket(size) {
    if (size < 1024 * 1024) return 'under_1mb';
    if (size < 5 * 1024 * 1024) return '1_to_5mb';
    return '5_to_12mb';
  }

  publishEvent(eventName, data = {}) {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    const variantId = this.querySelector('.product-variant-id')?.value || null;
    try {
      Promise.resolve(publish(`orlari:${eventName}`, {
        product_id: this.root.dataset.productId,
        variant_id: variantId,
        ...data
      })).catch(() => {});
    } catch (error) {
    }
  }

  async prepareImage(file) {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return file;

    const decoded = await this.decodeImage(file);
    const scale = Math.min(1, this.maxImageEdge / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });

    if (!context) {
      decoded.cleanup?.();
      return file;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(decoded.source, 0, 0, width, height);
    decoded.cleanup?.();

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => result ? resolve(result) : reject(new Error('Image compression failed')),
        'image/jpeg',
        this.imageCompressionQuality
      );
    });

    if (blob.size >= file.size) return file;
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'orlari-photo';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  }

  async decodeImage(file) {
    if ('createImageBitmap' in window) {
      try {
        const bitmap = await createImageBitmap(file);
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          cleanup: () => bitmap.close?.()
        };
      } catch (error) {
      }
    }

    const dataUrl = await this.fileToDataUrl(file);
    const image = new Image();
    image.decoding = 'async';

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error('Image decode failed'));
      image.src = dataUrl;
    });

    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      cleanup: null
    };
  }

  fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Photo preview failed'));
      reader.readAsDataURL(file);
    });
  }

  showPreview(file) {
    if (this.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
    const reader = new FileReader();
    reader.onload = () => {
      if (this.preparedFile !== file) return;
      this.previewUrl = String(reader.result || '');
      this.previewImage.src = this.previewUrl;
    };
    reader.readAsDataURL(file);
    this.fileName.textContent = 'Your photo';
    this.emptyState.hidden = true;
    this.preview.hidden = false;
  }

  resetPhoto(resetStatus = true) {
    this.selectionRequestId += 1;
    this.cancelPhotoUpload();
    if (this.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
    this.preparedFile = null;
    if (this.fileInput) this.fileInput.value = '';
    this.previewImage.removeAttribute('src');
    this.emptyState.hidden = false;
    this.preview.hidden = true;
    this.dropzone.classList.remove('is-ready');
    if (resetStatus) {
      this.clearStatusClasses();
      this.status.textContent = this.defaultGuidance;
    }
    this.syncPurchaseState();
  }

  showError(message) {
    this.clearStatusClasses();
    this.status.textContent = message;
    this.status.classList.add('is-error');
    this.dropzone.classList.add('is-error');
    this.dropzone.classList.remove('is-ready');
    this.updateUploadRetry();
  }

  clearStatusClasses() {
    this.status?.classList.remove('is-success', 'is-error', 'is-busy');
    if (this.uploadRetry) this.uploadRetry.hidden = true;
    this.dropzone?.classList.remove('is-error');
  }

  hasPhoto() {
    return Boolean(this.preparedFile);
  }

  syncPurchaseState() {
    if (!this.submitButton) return;
    if (this.submitting) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      return;
    }
    const ready = this.hasPhoto();
    const unavailable = this.querySelector('.product-variant-id')?.disabled;
    const label = this.submitButton.querySelector('span');

    if (!ready && !unavailable) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = 'Add your photo to continue';
    } else if (ready && !unavailable) {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-disabled');
      if (label) label.textContent = this.defaultSubmitText;
    }

    // Accelerated checkout can't reliably carry a file line-item property.
    // Keep it hidden for this personalized product so the photo cannot be bypassed.
    if (this.paymentButton) this.paymentButton.hidden = true;
    if (this.stickyButton) this.stickyButton.textContent = ready ? 'Add to cart' : 'Add your photo';
    this.updateStickyPrice();
  }

  guardPersonalization(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.hasPhoto()) {
      this.showError('Please add your photo before continuing.');
      this.dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.submitPersonalizedCart();
  }

  showCartPending(cart) {
    if (!cart?.querySelector('.drawer__inner')) return;
    let pending = cart.querySelector('[data-orlari-cart-pending]');

    if (!pending) {
      pending = document.createElement('div');
      pending.className = 'orlari-cart-pending';
      pending.dataset.orlariCartPending = '';
      pending.setAttribute('role', 'status');
      pending.setAttribute('aria-live', 'polite');

      const spinner = document.createElement('span');
      spinner.className = 'orlari-cart-pending__spinner';
      spinner.setAttribute('aria-hidden', 'true');

      const title = document.createElement('strong');
      title.dataset.orlariCartPendingTitle = '';

      const detail = document.createElement('p');
      detail.dataset.orlariCartPendingDetail = '';

      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--primary orlari-cart-pending__retry';
      retry.dataset.orlariCartRetry = '';
      retry.hidden = true;

      pending.append(spinner, title, detail, retry);
      cart.querySelector('.drawer__inner')?.append(pending);
    }

    pending.classList.remove('is-error');
    pending.querySelector('[data-orlari-cart-pending-title]').textContent =
      this.root.dataset.cartAttaching || 'Uploading your photo…';
    pending.querySelector('[data-orlari-cart-pending-detail]').textContent = '';
    const retry = pending.querySelector('[data-orlari-cart-retry]');
    retry.hidden = true;
    retry.onclick = null;
    cart.open(this.submitButton);
  }

  showCartError(cart, message) {
    if (!cart?.querySelector('.drawer__inner')) return;
    this.showCartPending(cart);
    const pending = cart.querySelector('[data-orlari-cart-pending]');
    if (!pending) return;

    pending.classList.add('is-error');
    pending.querySelector('[data-orlari-cart-pending-title]').textContent =
      this.root.dataset.cartFailed || "We couldn't add your bracelet.";
    pending.querySelector('[data-orlari-cart-pending-detail]').textContent =
      message || this.root.dataset.cartPhotoKept || 'Your photo is still selected.';

    const retry = pending.querySelector('[data-orlari-cart-retry]');
    retry.textContent = this.root.dataset.cartRetry || 'Try again';
    retry.hidden = false;
    retry.onclick = () => this.submitPersonalizedCart();
  }

  async submitPersonalizedCart() {
    if (this.submitting || !this.form || !this.preparedFile) return;

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    this.submitting = true;
    if (this.fileInput) this.fileInput.disabled = true;
    this.publishEvent('cart_add_started');
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.classList.add('loading');
    this.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.setBusyStatus(this.root.dataset.cartAdding || 'Adding to your cart…');
    cart?.setActiveElement?.(document.activeElement);

    const uploadAlreadyReady = this.uploadState === 'ready' && Boolean(this.uploadedPhotoUrl);
    if (!uploadAlreadyReady) this.showCartPending(cart);

    let cartRequestStarted = false;
    let cartRequestRejected = false;
    let cartTimeout;
    try {
      if (this.uploadState === 'error' || !this.uploadPromise) {
        this.startPhotoUpload(this.preparedFile);
      }

      const photoUrl = this.uploadedPhotoUrl || await this.uploadPromise;
      if (this.destroyed) return;
      if (!photoUrl) {
        throw new Error(this.uploadErrorMessage || this.root.dataset.photoUploadFailed || 'Your photo could not be uploaded. Check your connection and try again.');
      }

      const formData = new FormData(this.form);
      formData.set('properties[_Photo]', photoUrl);
      formData.set('properties[Photo]', this.root.dataset.photoUploaded || 'Uploaded');

      const sections = cart?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];
      if (sections.length) {
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
      }

      this.cartAbortController = new AbortController();
      cartTimeout = window.setTimeout(() => this.cartAbortController?.abort(), 20000);
      cartRequestStarted = true;
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData,
        signal: this.cartAbortController.signal
      });
      cartRequestRejected = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429;
      const payload = await response.json();

      if (!response.ok || payload.status) {
        cartRequestRejected = cartRequestRejected || (payload.status >= 400 && payload.status < 500 && payload.status !== 408 && payload.status !== 429);
        throw new Error(payload.description || payload.message || 'We could not add the bracelet. Please try again.');
      }

      if (this.destroyed) return;
      if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(payload);
      } else {
        this.goToCart();
      }
      this.clearStatusClasses();
      this.status.textContent = 'Photo ready';
      this.status.classList.add('is-success');
      this.publishEvent('cart_add_succeeded');
    } catch (error) {
      if (this.destroyed) return;
      if (cartRequestStarted && !cartRequestRejected) {
        this.goToCart();
        return;
      }
      const message = error?.message || 'We could not add the bracelet. Please try again.';
      const networkFailed = error instanceof TypeError || /failed to fetch/i.test(message);
      const customerMessage = networkFailed
        ? this.root.dataset.cartNetworkFailed || 'Your photo is still selected. Check your connection and try again.'
        : message;
      this.publishEvent('cart_add_failed', { error_type: networkFailed ? 'network' : 'cart_add' });
      this.showError(customerMessage);
      this.showCartError(cart, customerMessage);
    } finally {
      window.clearTimeout(cartTimeout);
      this.cartAbortController = null;
      this.submitting = false;
      if (this.fileInput) this.fileInput.disabled = false;
      this.updateUploadRetry();
      this.submitButton.classList.remove('loading');
      this.querySelector('.loading__spinner')?.classList.add('hidden');
      this.syncPurchaseState();
    }
  }

  handleStickyClick() {
    if (!this.hasPhoto()) {
      this.dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => this.fileInput.focus(), 450);
      return;
    }
    this.submitButton.click();
  }

  goToCart() {
    const url = new URL(window.routes?.cart_url || '/cart', window.location.href);
    const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    window.location.assign(url.toString());
  }

  updateStickyPrice() {
    if (!this.stickyPrice) return;
    const price = this.querySelector('.price__sale .price-item--last:not(.visually-hidden)')
      || this.querySelector('.price__regular .price-item--regular:not(s)');
    if (price) this.stickyPrice.textContent = price.textContent.trim();
  }

  updateStickyVisibility() {
    if (!this.sticky || !this.submitButton || window.innerWidth >= 750) {
      if (this.sticky) this.sticky.hidden = true;
      return;
    }

    const buttonRect = this.submitButton.getBoundingClientRect();
    const footer = document.querySelector('footer');
    const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
    this.sticky.hidden = !(buttonRect.bottom < 0 && !footerVisible);
  }
}

const orlariV2Instances = new WeakMap();

function getOrlariV2Roots(scope = document) {
  const roots = [];
  if (scope instanceof Element && scope.matches('.orlari-v2-product')) roots.push(scope);
  scope.querySelectorAll?.('.orlari-v2-product').forEach((root) => roots.push(root));
  return roots;
}

function initializeOrlariV2(scope = document) {
  getOrlariV2Roots(scope).forEach((root) => {
    if (orlariV2Instances.has(root)) return;
    orlariV2Instances.set(root, new OrlariV2Product(root));
  });
}

function destroyOrlariV2(scope) {
  getOrlariV2Roots(scope).forEach((root) => {
    orlariV2Instances.get(root)?.destroy();
    orlariV2Instances.delete(root);
  });
}

initializeOrlariV2();

document.addEventListener('shopify:section:load', (event) => initializeOrlariV2(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyOrlariV2(event.target));
